/**
 * Error Boundary Component
 * Prevents entire app from crashing when a component error occurs
 * Provides fallback UI and error reporting
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { devError } from '@/utils/devLog';
import * as Sentry from '@sentry/react';
import { analyticsService } from '@/services/analyticsService';
import { consentService } from '@/services/consentService';
import { ANALYTICS_FEATURES } from '@/constants/analytics';

type ErrorBoundaryLevel = 'app' | 'page' | 'section';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  level?: ErrorBoundaryLevel;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in their
 * child component tree, logs those errors, and displays a fallback UI.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error details
    devError('[ErrorBoundary] Caught error:', error);
    devError('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Send to Sentry
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        level: this.props.level || 'section'
      }
    });

    // Fire analytics event (class component can't use hooks)
    if (consentService.hasConsentFor('analytics')) {
      analyticsService.trackFeature(ANALYTICS_FEATURES.ERROR_BOUNDARY, 'app_error', {
        error_message: (error.message || '').slice(0, 100),
        component_level: this.props.level || 'section',
      });
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state if resetKeys changed
    if (this.state.hasError && this.props.resetKeys) {
      const keysChanged = this.props.resetKeys.some(
        (key, index) => prevProps.resetKeys?.[index] !== key
      );
      if (keysChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, level = 'section' } = this.props;

    if (!hasError) {
      return children;
    }

    // Return custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Return level-appropriate default fallback
    return (
      <ErrorFallback
        level={level}
        error={error}
        errorInfo={errorInfo}
        resetErrorBoundary={this.resetErrorBoundary}
      />
    );
  }
}

/**
 * Default error fallback UI based on error boundary level
 */
interface ErrorFallbackProps {
  level: ErrorBoundaryLevel;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  level,
  error,
  errorInfo,
  resetErrorBoundary
}) => {
  const isDevelopment = import.meta.env.DEV;

  if (level === 'app') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Something went wrong
          </h1>
          <p className="text-gray-600 mb-6">
            We're sorry, but something unexpected happened. Please try refreshing the page.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Page
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Home className="w-4 h-4 mr-2" />
              Go Home
            </button>
          </div>
          {isDevelopment && error && (
            <div className="mt-6 p-4 bg-gray-100 rounded-lg text-left">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                <Bug className="w-4 h-4" />
                Development Error Info
              </div>
              <pre className="text-xs text-red-600 overflow-auto max-h-32">
                {error.message}
              </pre>
              {errorInfo && (
                <pre className="text-xs text-gray-500 overflow-auto max-h-48 mt-2">
                  {errorInfo.componentStack}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (level === 'page') {
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-[400px]">
        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Unable to load this page
        </h2>
        <p className="text-gray-600 mb-4 text-center max-w-sm">
          An error occurred while loading this page. Please try again.
        </p>
        <button
          onClick={resetErrorBoundary}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </button>
        {isDevelopment && error && (
          <div className="mt-4 p-3 bg-gray-100 rounded-lg text-left max-w-md w-full">
            <pre className="text-xs text-red-600 overflow-auto">
              {error.message}
            </pre>
          </div>
        )}
      </div>
    );
  }

  // Section level (default)
  return (
    <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border border-gray-200">
      <AlertTriangle className="w-8 h-8 text-gray-400 mb-2" />
      <p className="text-gray-600 text-sm mb-3">
        This section couldn't be loaded
      </p>
      <button
        onClick={resetErrorBoundary}
        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
      >
        <RefreshCw className="w-3 h-3" />
        Retry
      </button>
    </div>
  );
};

/**
 * HOC to wrap components with error boundary
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.FC<P> {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
}

export default ErrorBoundary;
